import { useLessonClient } from "@/contexts/LessonClientProvider";
import { EducationLevel, INSTRUCTION_MAX_LENGTH, KnowledgeGenerationSettingsDTO, LessonDTO, REJECT_REASONS, ScenarioGenerationSettingsDTO } from "@superlekcja/shared";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ActiveViewParams, ChangeView, LessonCommand } from "./types";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { useErrorHandler } from "@/contexts/ErrorHandlerContext";
import { getItemPath, parseItemPath, SlidesContentDTO } from "@superlekcja/shared";
import { LessonReadyModal } from "./lesson-ready-modal";
import { parseScenarioStructure } from "@superlekcja/shared";
import { track } from "@/utils/analytics";
import { SlidesPresenter } from "./slides/slides-presenter";
import { InstructionRejectedError } from "@/services/lesson-client";

/**
 * Reguly, ktore `instructionSchema` egzekwuje zodem po stronie backendu — powtorzone tutaj NIE po
 * to, zeby zastapic walidacje serwera, tylko zeby dalo sie powiedziec nauczycielowi, o co chodzi.
 * Odrzucenie zodem wraca przez `validateRequest` bez `message` i bez `code`, wiec bez tego
 * sprawdzenia "dla uczniów < 10 lat" konczy sie generycznym toastem, z ktorego nic nie wynika.
 */
function localInstructionError(instruction: string): string | null {
    if (/[<>]/.test(instruction)) return 'instruction-error-angle-brackets';
    if (instruction.length > INSTRUCTION_MAX_LENGTH) return 'instruction-error-too-long';
    return null;
}

/**
 * Odmowa modyfikacji na klucz i18n. Klucze sa plaskie (i18n/index.ts tlumaczy dlaczego).
 * Nieznany kod/powod dostaje komunikat ogolny — nauczyciel nigdy nie widzi kodu maszynowego.
 * Lista powodow przychodzi z @superlekcja/shared — ta sama, ktorej pilnuje schemat analizatora.
 */
function instructionErrorKey(error: unknown): string | null {
    if (!(error instanceof InstructionRejectedError)) return null;
    switch (error.code) {
        case 'instruction-rejected':
            // nieznany powod -> komunikat ogolny; inaczej i18next wyswietlilby surowy klucz
            return error.reason && (REJECT_REASONS as readonly string[]).includes(error.reason)
                ? `instruction-error-rejected-${error.reason}`
                : 'instruction-error-rejected';
        case 'instruction-too-long':
            return 'instruction-error-too-long';
        case 'instruction-empty':
            return 'instruction-error-empty';
        case 'scenario-not-modifiable':
        case 'knowledge-not-modifiable':
            return 'instruction-error-not-modifiable';
        default:
            return 'instruction-error-rejected';
    }
}

const ctx = createContext<{ lessonContext: LessonEditorContextType, dispatch: (cmd: LessonCommand) => void }>({} as any)

type LessonEditorContextType = {
    activeViewParams: ActiveViewParams,
    lesson: LessonDTO | null,
    scenarioSettings: ScenarioGenerationSettingsDTO,
    knowledgeSettings: KnowledgeGenerationSettingsDTO,
    showSolutions: boolean,
    /** tekst polecenia wpisany przez nauczyciela; pusty = zwykla regeneracja */
    scenarioInstruction: string,
    knowledgeInstruction: string,
    /** klucz i18n komunikatu o odrzuceniu polecenia, null = brak bledu */
    scenarioInstructionError: string | null,
    knowledgeInstructionError: string | null,
    hasScenarioSettingsChanged: boolean,
    hasKnowledgeSettingsChanged: boolean
    isEditable: boolean
    openPresenter: () => void
    closePresenter: () => void
}

export function useLessonEditorContext() {
    const lessonContext = useContext(ctx)

    return lessonContext
}


export function LessonEditorContextProvider({ children }: { children: React.ReactNode }) {

    const { lessonId, section, itemId: itemPath } = useParams<{ lessonId: string, section?: string, itemId?: string }>()

    const lessonClient = useLessonClient()
    const { handleError } = useErrorHandler();

    const { user, withUser } = useAuth()
    const [lesson, setLesson] = useState<LessonDTO | null>(null)
    const [scenarioSettings, setScenarioSettings] = useState<ScenarioGenerationSettingsDTO>({
        duration: 45,
        educationLevel: EducationLevel.AUTO
    })
    const [scenarioInstruction, setScenarioInstruction] = useState('')
    const [scenarioInstructionError, setScenarioInstructionError] = useState<string | null>(null)
    const [knowledgeSettings, setKnowledgeSettings] = useState<KnowledgeGenerationSettingsDTO>({ educationLevel: EducationLevel.AUTO });
    const [knowledgeInstruction, setKnowledgeInstruction] = useState('')
    const [knowledgeInstructionError, setKnowledgeInstructionError] = useState<string | null>(null)
    const [showWorksheetSolutions, setShowWorksheetSolutions] = useState(false);
    const [presentingDeck, setPresentingDeck] = useState<SlidesContentDTO | null>(null)
    const [readyModal, setReadyModal] = useState<{ topic: string } | null>(null)

    // parse current location from URL
    const activeViewParams = useMemo((): ActiveViewParams => {
        if (section === 'knowledge') return { section: 'knowledge' };
        if (section === 'slides') return { section: 'slides' };
        if (section === 'scenario') return { section: 'scenario' };

        if (section === 'worksheet') {
            if (itemPath && lesson?.worksheet) {
                const itemId = parseItemPath(itemPath, lesson.worksheet) ?? undefined
                return { section: 'worksheet', itemId };
            } else {
                return { section: 'worksheet' };
            }
        }

        return { section: 'index' };
    }, [section, itemPath, lesson?.worksheet]);

    const [currentLocation, setCurrentLocation] = useState<ChangeView["data"]|null>(null)

    useEffect(() => {
        const subs = [
            lessonClient.lesson$.subscribe(setLesson),
            lessonClient.autogenComplete$.subscribe(({ topic }) => setReadyModal({ topic })),
        ]
        return () => {
            subs.forEach(sub => sub.unsubscribe())
        }
    }, [lessonClient])
    
    useEffect(() => {
        if (lessonId) {
            lessonClient.getLesson(lessonId)
        }
    }, [lessonClient,lessonId])

    useEffect(() => {
        if (lesson?.scenario.prompt) {
            setScenarioSettings({ ...lesson.scenario.prompt.settings })
            resetScenarioInstruction()
        }
        if (lesson?.knowledge?.prompt) {
            setKnowledgeSettings({ ...lesson.knowledge.prompt.settings })
            resetKnowledgeInstruction()
        }
    }, [lesson?.id])

    useEffect(() => {
        if (currentLocation) {
            if (!lesson?.slug) {
                return
            }
            if (currentLocation.itemType === activeViewParams.section && currentLocation.itemId === (activeViewParams as any).itemId) {
                return
            }
            switch (currentLocation.itemType) {
                case 'index':
                    navigate(`/lessons/${lesson?.slug}`)
                    break;
                case 'scenario':
                    navigate(`/lessons/${lesson?.slug}/scenario`)
                    break;
                case 'knowledge':
                    navigate(`/lessons/${lesson?.slug}/knowledge`)
                    break;
                case 'slides':
                    navigate(`/lessons/${lesson?.slug}/slides`)
                    break;
                case 'worksheet':
                    if (currentLocation.itemId) {
                        const itemPath = getItemPath(lesson?.worksheet!, currentLocation.itemId)
                        navigate(`/lessons/${lesson?.slug}/worksheet/${itemPath}`)
                    } else {
                        navigate(`/lessons/${lesson?.slug}/worksheet`)
                    }
                    break;
            }
        }
    }, [currentLocation])

    const resetScenarioInstruction = () => { setScenarioInstruction(''); setScenarioInstructionError(null) }
    const resetKnowledgeInstruction = () => { setKnowledgeInstruction(''); setKnowledgeInstructionError(null) }

    const originalScenarioSettings = lesson?.scenario?.prompt?.settings;
    const hasScenarioSettingsChanged =
        scenarioSettings.duration !== originalScenarioSettings?.duration || scenarioInstruction.trim().length > 0;

    const hasKnowledgeSettingsChanged = knowledgeInstruction.trim().length > 0;

    const openPresenter = () => {
        if (!lesson?.slides?.content) return
        track('slides_presented')
        setPresentingDeck(lesson.slides.content)
    }
    const closePresenter = () => setPresentingDeck(null)

    const lessonContext: LessonEditorContextType = {
        activeViewParams,
        lesson,
        scenarioSettings,
        knowledgeSettings,
        scenarioInstruction,
        knowledgeInstruction,
        scenarioInstructionError,
        knowledgeInstructionError,
        showSolutions: showWorksheetSolutions,
        hasScenarioSettingsChanged,
        hasKnowledgeSettingsChanged,
        isEditable: lesson?.userId === user?.id,
        openPresenter,
        closePresenter,
    }


    const navigate = useNavigate()
    const dispatch = async (cmd: LessonCommand) => {
        switch (cmd.type) {
            case 'copy-lesson': {
                {
                    if (!lesson?.id) return
                    withUser(async () => {
                        try {
                            const copiedLesson = await lessonClient.copy()
                            navigate(`/lessons/${copiedLesson.slug}`)
                            toast({
                                title: "Sukces",
                                description: "Lekcja została skopiowana.",
                            })
                        } catch (error) {
                            handleError(error, "Nie udało się skopiować lekcji.");
                        }
                    })
                }
                break;
            }

            case 'regenerate': {
                switch (cmd.data.itemType) {
                    case 'scenario': {
                        // Odmowa przychodzi PRZED pobraniem kredytu i przed otwarciem strumienia,
                        // wiec nie czyscimy pola: nauczyciel ma poprawic swoj tekst, nie pisac go
                        // od nowa. Nic sie tez nie wygenerowalo, wiec nie ma za co placic.
                        setScenarioInstructionError(null)
                        const instruction = scenarioInstruction.trim()
                        const localError = instruction ? localInstructionError(instruction) : null
                        if (localError) {
                            setScenarioInstructionError(localError)
                            break;
                        }
                        track('lesson_modified', { section: 'scenario', has_instruction: !!instruction })
                        try {
                            await lessonClient.modifyScenario({
                                duration: scenarioSettings.duration,
                                ...(instruction ? { instruction } : {})
                            })
                            resetScenarioInstruction()
                        } catch (error) {
                            const key = instructionErrorKey(error)
                            if (key) setScenarioInstructionError(key)
                            else handleError(error, "Nie udało się zmodyfikować scenariusza.")
                        }
                        break;
                    }
                    case 'knowledge': {
                        setKnowledgeInstructionError(null)
                        const instruction = knowledgeInstruction.trim()
                        const localError = instruction ? localInstructionError(instruction) : null
                        if (localError) {
                            setKnowledgeInstructionError(localError)
                            break;
                        }
                        track('lesson_modified', { section: 'knowledge', has_instruction: !!instruction })
                        try {
                            await lessonClient.createOrModifyKnowledge(instruction ? { instruction } : {})
                            resetKnowledgeInstruction()
                        } catch (error) {
                            const key = instructionErrorKey(error)
                            if (key) setKnowledgeInstructionError(key)
                            else handleError(error, "Nie udało się zmodyfikować kompendium.")
                        }
                        break;
                    }
                    case 'slides':
                        track('lesson_modified', { section: 'slides', has_instruction: !!cmd.data.instruction })
                        await lessonClient.createOrModifySlides({
                            prompt: {
                                instruction: cmd.data.instruction
                            }
                        })
                        break;
                }
                break;
            }
            // the problem is that sometimes this command is called in reaction to event
            // that has not been propagated to .lesson yet, and we operate on stale data.
            case 'change-view':
                setCurrentLocation(cmd.data);
                break;
            case 'change-settings': {
                switch (cmd.data.itemType) {
                    case 'scenario':
                        setScenarioSettings(cmd.data.settings)
                        break;
                    case 'knowledge':
                        setKnowledgeSettings(cmd.data.settings)
                        break;
                }
                break;
            }
            case 'change-show-solutions': {
                setShowWorksheetSolutions(cmd.data.showSolutions)
                break;
            }
            case 'change-version': {
                switch (cmd.data.itemType) {
                    case 'scenario':
                        await lessonClient.version('scenario', cmd.data.step, (lesson)=>{
                            setScenarioSettings(lesson?.scenario?.prompt?.settings ?? { duration: 45, educationLevel: EducationLevel.AUTO })
                                resetScenarioInstruction()
                            })
                            break;
                    case 'knowledge':
                        await lessonClient.version('knowledge', cmd.data.step, (lesson)=>{
                            setKnowledgeSettings(lesson?.knowledge?.prompt?.settings ?? {  })
                            resetKnowledgeInstruction()
                        })
                        break;
                    case 'slides':
                        await lessonClient.version('slides', cmd.data.step, (lesson)=>{})
                        break;
                    case 'worksheet':
                        await lessonClient.version('worksheet', cmd.data.step, (lesson)=>{})
                        break;
                    case 'image':
                        await lessonClient.version('image', cmd.data.step, (lesson)=>{})
                        break;
                }
                break;
            }
            case 'revert-changes': {
                switch (cmd.data.itemType) {
                    case 'scenario':
                        if (lesson?.scenario.prompt) setScenarioSettings(lesson?.scenario.prompt.settings)
                        resetScenarioInstruction()
                        break;
                    case 'knowledge':
                        if (lesson?.knowledge?.prompt) setKnowledgeSettings(lesson?.knowledge.prompt.settings)
                        resetKnowledgeInstruction()
                        break;
                }
                break;
            }
            case 'set-instruction': {
                switch (cmd.data.itemType) {
                    case 'scenario':
                        setScenarioInstruction(cmd.data.instruction)
                        setScenarioInstructionError(null)
                        break;
                    case 'knowledge':
                        setKnowledgeInstruction(cmd.data.instruction)
                        setKnowledgeInstructionError(null)
                        break;
                }
                break;
            }
            case 'append-instruction': {
                // limit pilnowany tez tutaj, bo dopisanie omija maxLength pola
                const append = (prev: string) => (prev ? `${prev.trimEnd()} ` : '').concat(cmd.data.text).slice(0, INSTRUCTION_MAX_LENGTH)
                switch (cmd.data.itemType) {
                    case 'scenario':
                        setScenarioInstruction(append)
                        setScenarioInstructionError(null)
                        break;
                    case 'knowledge':
                        setKnowledgeInstruction(append)
                        setKnowledgeInstructionError(null)
                        break;
                }
                break;
            }
            case 'add-material': {    
                await lessonClient.generateWorksheetItem(cmd.data.materialTemplateIndex, null)
                break;
            }

            case 'regenerate-worksheet-item': {
                await lessonClient.generateWorksheetItem(null, cmd.data.id)
                break;
            }

            case 'delete-lesson': {
                if (!lesson?.id) return
                withUser(async () => {
                    try {
                        await lessonClient.delete()
                        navigate(`/`)
                        toast({
                            title: "Sukces",
                            description: "Lekcja została usunięta.",
                        })
                    } catch (error) {
                        handleError(error, "Nie udało się usunąć lekcji.");
                    }
                })
                break;
            }

            
            case 'remove-worksheet-item': {
                await lessonClient.removeWorksheetItem(cmd.data.id)
                break;
            }

            case 'set-public': {
                if (!lesson?.id) return
                await lessonClient.updateLesson({ public: cmd.data.isPublic })
                break;
            }

            case 'set-browsable': {
                if (!lesson?.id) return
                await lessonClient.updateLesson({
                    ...(cmd.data.isBrowsable !== undefined ? { browsable: cmd.data.isBrowsable } : {}),
                    ...(cmd.data.browsableFrom !== undefined ? { browsableFrom: cmd.data.browsableFrom } : {}),
                })
                break;
            }

            case 'set-interactive': {
                if (!lesson?.id) return
                await lessonClient.updateLesson({ interactive: cmd.data.isInteractive })
                break;
            }

            case 'generate-image': {
                if (!lesson?.id) return
                await lessonClient.generateImage()
                break;
            }

            case 'interactive-view' : {
                if (lesson?.worksheet) {
                    const itemPath = getItemPath(lesson.worksheet, cmd.data.itemId)
                    navigate(`/i/${lesson!.slug}/${itemPath}`);   
                }
                break;
            }

            default:
                break
        }
    }

    return <ctx.Provider value={{ lessonContext, dispatch }}>
        {children}
        {readyModal && (
            <LessonReadyModal
                // Temat ze zdarzenia to wpis uzytkownika; scenariusz nadaje lekcji
                // wlasny tytul i to on stoi w naglowku tuz pod oknem.
                topic={parseScenarioStructure(lesson?.scenario.content?.content ?? '').title || readyModal.topic}
                imageUrl={lesson?.image?.publicUrl}
                open={true}
                onPresent={() => { setReadyModal(null); openPresenter() }}
                onClose={() => setReadyModal(null)}
            />
        )}
        {presentingDeck && (
            <SlidesPresenter
                deck={presentingDeck}
                onClose={closePresenter}
                titleImageUrl={lesson?.image?.publicUrl}
            />
        )}
    </ctx.Provider>
}
